/* Adenzo mobile app layer — sheets (phase 4).
   Works with the theme's existing drawers and modals; it does not replace them:
   - cart drawer ........ bottom sheet (styled in adenzo-app.css), drag the header down to dismiss
   - quick-add / modals .. bottom sheet, drag the handle down to dismiss
   - menu drawer ........ drag left to dismiss (the filter drawer is left to facets.js)
   - Android back button / iOS edge swipe closes the topmost open sheet instead of leaving the page.
   Sets html.adenzo-sheet-open while any sheet is open (the tab bar and install card step aside). */
(function () {
  'use strict';
  if (window.AdenzoSheets) return;

  var root = document.documentElement;
  var registry = {};
  var stack = [];
  var hasEntry = false; // we pushed one history entry for "a sheet is open"
  var suppress = 0; // popstate events we caused ourselves (history.back after a normal close)
  var settleTimer = null;
  var pendingTimer = null;
  var uid = 0;
  var ids = typeof WeakMap === 'function' ? new WeakMap() : null;

  function isApp() {
    return !!(window.AdenzoApp && window.AdenzoApp.isApp());
  }

  function observe(el, options, callback) {
    if (!el || !window.MutationObserver) return;
    new MutationObserver(callback).observe(el, options);
  }

  /* ---- open-sheet stack + one history entry ------------------------------ */

  function opened(id) {
    if (stack.indexOf(id) === -1) stack.push(id);
    update();
  }

  function closed(id) {
    var index = stack.indexOf(id);
    if (index > -1) stack.splice(index, 1);
    update();
  }

  function update() {
    root.classList.toggle('adenzo-sheet-open', stack.length > 0);
    window.clearTimeout(settleTimer);
    // Debounced so close-then-open handoffs (cart -> quick add) do not thrash the history.
    settleTimer = window.setTimeout(settle, 120);
  }

  var traversing = false; // a history.back() we started is still in flight
  var traverseTimer = null;

  function startBack() {
    suppress++;
    traversing = true;
    window.clearTimeout(traverseTimer);
    // If the browser never reports the traversal, do not swallow the user's next real Back.
    traverseTimer = window.setTimeout(function () {
      if (traversing) { traversing = false; suppress = Math.max(0, suppress - 1); }
    }, 800);
    window.history.back();
  }

  function settle() {
    if (!isApp()) return;
    if (traversing) { // serialise: never push while a pop is still pending
      settleTimer = window.setTimeout(settle, 150);
      return;
    }
    var want = stack.length > 0;
    if (want && !hasEntry) {
      // Without a user gesture some browsers mark the entry skippable on Back, so do not rely on it.
      if (window.navigator.userActivation && !window.navigator.userActivation.hasBeenActive) return;
      try {
        window.history.pushState({ adenzoSheet: true }, '', window.location.href);
        hasEntry = true;
      } catch (e) { /* history not available: sheets still work, back just leaves the page */ }
    } else if (!want && hasEntry) {
      hasEntry = false;
      startBack();
    }
  }

  window.addEventListener('popstate', function () {
    if (suppress > 0) {
      suppress--;
      traversing = false;
      window.clearTimeout(traverseTimer);
      if (pendingNav && suppress === 0) go();
      return;
    }
    hasEntry = false; // the browser just removed our entry: this is the user's Back
    var top = stack[stack.length - 1];
    if (top && registry[top]) registry[top].close();
  });

  var pendingNav = null; // {href} or {form, submitter}: navigation to continue once our entry is popped

  function go() {
    var nav = pendingNav;
    pendingNav = null;
    window.clearTimeout(pendingTimer);
    if (!nav) return;
    if (nav.form) {
      try { nav.form.requestSubmit(nav.submitter || undefined); } catch (e) { nav.form.submit(); }
    } else if (nav.href) {
      window.location.assign(nav.href);
    }
  }

  function leaveWith(nav) {
    pendingNav = nav;
    hasEntry = false;
    startBack();
    pendingTimer = window.setTimeout(go, 700);
  }

  var SHEET_HOSTS = 'cart-drawer, .menu-drawer, modal-dialog, quick-add-modal, product-modal';

  // A link or form followed from inside a sheet must not leave our extra history entry behind
  // (Back would land on the same page again). Step back first, then continue. Bubble phase, so anything
  // that already handled the click (and called preventDefault) is left alone.
  document.addEventListener('click', function (event) {
    if (!hasEntry || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (!link || !link.closest(SHEET_HOSTS)) return;
    var raw = link.getAttribute('href') || '';
    if (raw.charAt(0) === '#' || /^(javascript|mailto|tel):/i.test(raw) || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    event.preventDefault();
    leaveWith({ href: link.href });
  });

  // Same for forms (the cart's Checkout button submits a form).
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!hasEntry || event.defaultPrevented || !form || !form.closest || !form.closest(SHEET_HOSTS) && !form.matches('#CartDrawer-Form')) return;
    if (form.target && form.target !== '_self') return;
    event.preventDefault();
    leaveWith({ form: form, submitter: event.submitter || null });
  });

  // Arrived (Back/Forward) on a page whose entry we had pushed, with nothing open: step past it once.
  try {
    var nav = window.performance && window.performance.getEntriesByType && window.performance.getEntriesByType('navigation')[0];
    if (nav && nav.type === 'back_forward' && window.history.state && window.history.state.adenzoSheet) {
      startBack();
    }
  } catch (e) { /* ignore */ }

  /* ---- drag to dismiss ---------------------------------------------------- */

  function dismissible(host, config) {
    host.addEventListener('pointerdown', function (event) {
      if (!isApp() || (event.pointerType === 'mouse' && event.button > 0)) return;
      var panel = config.panel(event);
      if (!panel || !config.handle(event, panel)) return;

      var startX = event.clientX;
      var startY = event.clientY;
      var lastY = startY;
      var lastX = startX;
      var lastT = event.timeStamp;
      var velocity = 0;
      var dragging = false;
      var pointerId = event.pointerId;
      var vertical = config.axis === 'y';

      function offset(x, y) {
        return vertical ? Math.max(0, y - startY) : Math.min(0, x - startX);
      }

      function move(e) {
        if (e.pointerId !== pointerId) return;
        var along = Math.abs(vertical ? e.clientY - startY : e.clientX - startX);
        var across = Math.abs(vertical ? e.clientX - startX : e.clientY - startY);
        var amount = offset(e.clientX, e.clientY);
        if (!dragging) {
          if (amount === 0 || along < 8 || along < across) return;
          dragging = true;
          panel.style.transition = 'none';
          try { panel.setPointerCapture(pointerId); } catch (err) { /* ignore */ }
        }
        var dt = Math.max(1, e.timeStamp - lastT);
        velocity = vertical ? (e.clientY - lastY) / dt : (lastX - e.clientX) / dt;
        lastY = e.clientY;
        lastX = e.clientX;
        lastT = e.timeStamp;
        panel.style.transform = vertical ? 'translateY(' + amount + 'px)' : 'translateX(' + amount + 'px)';
      }

      function finish(e) {
        if (e.pointerId !== pointerId) return;
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', finish);
        document.removeEventListener('pointercancel', finish);
        if (!dragging) return;
        var amount = Math.abs(offset(e.clientX, e.clientY));
        var size = vertical ? panel.offsetHeight : panel.offsetWidth;
        var dismiss = e.type !== 'pointercancel' && (amount > Math.min(140, size * 0.3) || (velocity > 0.6 && amount > 40));
        // The drag must not turn into a tap on whatever is under the finger.
        var swallow = function (ev) { ev.stopPropagation(); ev.preventDefault(); };
        window.addEventListener('click', swallow, true);
        window.setTimeout(function () { window.removeEventListener('click', swallow, true); }, 350);
        panel.style.transition = 'transform 0.2s ease';
        if (dismiss) {
          if (window.AdenzoApp && window.AdenzoApp.haptic) window.AdenzoApp.haptic(10);
          panel.style.transform = vertical ? 'translateY(100%)' : 'translateX(-100%)';
          window.setTimeout(function () {
            config.close(panel);
            panel.style.transition = '';
            panel.style.transform = '';
          }, 190);
        } else {
          panel.style.transform = '';
          window.setTimeout(function () { panel.style.transition = ''; }, 220);
        }
      }

      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', finish);
      document.addEventListener('pointercancel', finish);
    });
  }

  /* ---- cart drawer -------------------------------------------------------- */

  var cart = document.querySelector('cart-drawer');
  if (cart) {
    registry.cart = { close: function () { if (typeof cart.close === 'function') cart.close(); } };
    var syncCart = function () { if (cart.classList.contains('active')) opened('cart'); else closed('cart'); };
    observe(cart, { attributes: true, attributeFilter: ['class'] }, syncCart);
    syncCart();
    dismissible(cart, {
      axis: 'y',
      panel: function () { return cart.querySelector('.drawer__inner'); },
      handle: function (event) { return !!event.target.closest('.drawer__header'); },
      close: function () { registry.cart.close(); }
    });
  }

  /* ---- menu and filter drawers (details-based) ---------------------------- */

  Array.prototype.forEach.call(document.querySelectorAll('header-drawer, menu-drawer'), function (drawer, index) {
    var details = drawer.querySelector('details');
    if (!details) return;
    // The filter drawer is not a sheet: facets.js owns history on collection pages (it pushes its own
    // entries and re-renders the grid from them), so we must not add ours. The tab bar still steps aside via body scroll-lock.
    if (drawer.classList.contains('mobile-facets__wrapper')) return;
    var id = 'menu-' + index;
    registry[id] = {
      close: function () {
        if (typeof drawer.closeMenuDrawer === 'function') drawer.closeMenuDrawer(new Event('adenzo-sheet'), details.querySelector('summary'));
      }
    };
    var sync = function () {
      if (details.hasAttribute('open') && details.classList.contains('menu-opening')) opened(id); else closed(id);
    };
    observe(details, { attributes: true, attributeFilter: ['class', 'open'] }, sync);
    sync();
    // Only the slide-in menu itself is swipe-dismissed; filters keep their own drawer behaviour.
    if (drawer.tagName === 'HEADER-DRAWER') {
      dismissible(drawer, {
        axis: '-x',
        panel: function (event) { return event.target.closest('.menu-drawer'); },
        handle: function () { return true; },
        close: function () { registry[id].close(); }
      });
    }
  });

  /* ---- modals (quick add, size guide, media...) --------------------------- */

  var MODAL = 'modal-dialog, quick-add-modal, product-modal';

  function modalId(modal) {
    if (!ids) { modal.__adenzoSheetId = modal.__adenzoSheetId || ++uid; return 'modal-' + modal.__adenzoSheetId; }
    if (!ids.has(modal)) ids.set(modal, ++uid);
    return 'modal-' + ids.get(modal);
  }

  function addHandle(modal) {
    var content = modal.querySelector('.quick-add-modal__content');
    if (!content || content.querySelector('.adenzo-sheet-handle')) return;
    var handle = document.createElement('div');
    handle.className = 'adenzo-sheet-handle';
    handle.setAttribute('aria-hidden', 'true');
    content.insertBefore(handle, content.firstChild);
  }

  function syncModal(modal) {
    var id = modalId(modal);
    registry[id] = { close: function () { if (typeof modal.hide === 'function') modal.hide(); } };
    if (modal.hasAttribute('open')) {
      addHandle(modal);
      opened(id);
    } else {
      closed(id);
    }
  }

  observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] }, function (records) {
    records.forEach(function (record) {
      var target = record.target;
      if (target.matches && target.matches(MODAL)) syncModal(target);
    });
  });
  Array.prototype.forEach.call(document.querySelectorAll(MODAL), function (modal) {
    if (modal.hasAttribute('open')) syncModal(modal);
  });

  dismissible(document, {
    axis: 'y',
    panel: function (event) { return event.target.closest ? event.target.closest('.quick-add-modal__content') : null; },
    handle: function (event) { return !!event.target.closest('.adenzo-sheet-handle'); },
    close: function (panel) {
      var modal = panel.closest(MODAL);
      if (modal && typeof modal.hide === 'function') modal.hide();
    }
  });

  window.AdenzoSheets = {
    isOpen: function () { return stack.length > 0; },
    closeTop: function () {
      var top = stack[stack.length - 1];
      if (top && registry[top]) registry[top].close();
    }
  };
})();
